import React from "react"
import PropTypes from "prop-types"
import tw from "twin.macro"
import { navigate } from "gatsby"
import AnchorLink from "react-anchor-link-smooth-scroll"

import binocularIcon from "../images/icons/icon-binocular-yellow.svg"

/***
 *
 * variant = "yellow(default),blue,white"
 * env = "white(default), yellow, blue"
 * hover: custom hover styles can be passed in as an object containing keys "bg" and "text"
 */

const ButtonStyle = tw`flex items-center font-semibold tracking-tight text-sm py-2 px-5 rounded-full transition border-3 md:(text-base leading-none py-5) lg:(leading-none) focus:outline-none`
export const Button = ({
  variant = "yellow",
  env = "white",
  hover,
  size,
  arrow,
  download,
  downloadBlue,
  children,
  as = "button",
  addClassForGTM = "",
  fillWidth,
  mobile,
  elementName,
  ariaLabel,
  styles,
  back,
  ...props
}) => {
  const yellowVar = tw`bg-yellow border-yellow text-blue`
  const blueVar = tw`bg-blue border-blue text-yellow`
  const whiteVar = tw`bg-white border-white text-blue`
  const lightBlueVar = tw`bg-lightblue border-lightblue text-white`

  const whiteEnv =
    variant === "yellow"
      ? tw`hover:bg-blue hover:border-blue hover:text-yellow active:border-blue focus:outline-none focus-visible:ring-4 focus-visible:ring-blue`
      : tw`hover:bg-yellow hover:border-yellow hover:text-blue active:border-yellow focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow`
  const blueEnv =
    variant === "yellow"
      ? tw`hover:bg-white hover:border-white hover:text-blue active:border-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white`
      : tw`hover:bg-yellow hover:border-yellow hover:text-blue active:border-yellow focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow`
  const yellowEnv =
    variant === "blue"
      ? tw`text-white hover:bg-white hover:border-white hover:text-blue active:border-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white`
      : tw`hover:bg-blue hover:border-blue hover:text-yellow active:border-blue focus:outline-none focus-visible:ring-4 focus-visible:ring-blue`
  let ButtonBase = "button"

  if (as === "link") {
    ButtonBase = "a"
  } else if (as === "anchor") {
    ButtonBase = AnchorLink
  }

  return (
    <ButtonBase
      {...props}
      data-elementname={elementName}
      aria-label={ariaLabel}
      className={`gtm-cta-click group ${addClassForGTM && `${addClassForGTM}`}`}
      css={[
        ButtonStyle,
        variant === "yellow" && yellowVar,
        variant === "blue" && blueVar,
        variant === "lightblue" && lightBlueVar,
        variant === "white" && whiteVar,
        env === "yellow" && yellowEnv,
        env === "blue" && blueEnv,
        env === "white" && whiteEnv,
        fillWidth && tw`flex items-center justify-between w-full`,
        size === "xs" && tw`text-sm px-5 border-2 w-max md:(text-xs py-1.5)`,
        size === "sm" &&
          tw`text-sm px-6 border-2 md:(text-sm py-2) lg:(px-4) xl:(px-6)`,
        mobile && tw`px-4`,
        hover &&
          `&:hover { background: ${hover.bg}; color: ${hover.text}; border-color: ${hover.bg} }`,
        back && tw`flex-row-reverse`,
        styles,
      ]}
    >
      {children}
      {arrow && (
        <div
          css={[
            tw`inline-block group-hover:animate-bouncex`,
            back && tw`group-hover:animate-bouncexalt`,
          ]}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="16"
            viewBox="0 0 10 16"
            tw="flex-shrink-0 stroke-current text-blue group-hover:text-yellow inline-block ml-4 lg:ml-2 xl:ml-5"
            css={[
              variant === "blue" && tw`text-yellow group-hover:text-blue`,
              variant === "lightblue" && tw`text-white group-hover:text-blue`,
              env === "blue" && tw`group-hover:text-blue`,
              (size === "xs" || size === "sm") && tw`h-2.5`,
              variant === "blue" && env === "yellow" && tw`text-white`,
              back && tw`ml-0 mr-4 rotate-180 lg:(ml-0 mr-2) xl:(ml-0 mr-5)`,
            ]}
          >
            <g>
              <g transform="rotate(-315 5 8) translate(0 2)">
                <path
                  fill="none"
                  strokeLinecap="square"
                  strokeMiterlimit="20"
                  strokeWidth="3"
                  d="M-1.34 4v0l7.724.276v0L6.66 12v0"
                />
              </g>
            </g>
          </svg>
        </div>
      )}
      {download && (
        <div
          css={[
            tw`bg-yellow w-10 h-10 flex justify-center items-center rounded-full ml-4 lg:ml-2 xl:ml-5`,
          ]}
        >
          <svg width="18px" height="22px" viewBox="0 0 18 22">
            <g>
              <g transform="translate(-11.000000, -8.000000)">
                <g transform="translate(11.000000, 10.000000)">
                  <path
                    tw="stroke-current text-blue transition group-hover:(text-blue)"
                    d="M5.30582126,1.88599218 L13.4367546,1.88599218 L13.4367546,9.88599218 M12.9709873,2.25270317 L4.49691323,10.5903183"
                    stroke="none"
                    fill="none"
                    strokeWidth="2.625"
                    transform="translate(8.966834, 6.238155) rotate(-225.000000) translate(-8.966834, -6.238155) "
                  ></path>
                  <rect
                    tw="fill-current text-blue transition group-hover:(text-blue)"
                    fill="none"
                    x="0"
                    y="16.8037053"
                    width="18"
                    height="2.63"
                  ></rect>
                </g>
              </g>
            </g>
          </svg>
        </div>
        )}
        {downloadBlue && (
        <div
          css={[
            tw`bg-blue w-10 h-10 flex justify-center items-center rounded-full ml-4 lg:ml-2 xl:ml-5`,
          ]}
        >
          <svg width="18px" height="22px" viewBox="0 0 18 22">
            <g>
              <g transform="translate(-11.000000, -8.000000)">
                <g transform="translate(11.000000, 10.000000)">
                  <path
                    tw="stroke-current text-yellow transition group-hover:(text-yellow)"
                    d="M5.30582126,1.88599218 L13.4367546,1.88599218 L13.4367546,9.88599218 M12.9709873,2.25270317 L4.49691323,10.5903183"
                    stroke="none"
                    fill="none"
                    strokeWidth="2.625"
                    transform="translate(8.966834, 6.238155) rotate(-225.000000) translate(-8.966834, -6.238155) "
                  ></path>
                  <rect
                    tw="fill-current text-yellow transition group-hover:(text-yellow)"
                    fill="none"
                    x="0"
                    y="16.8037053"
                    width="18"
                    height="2.63"
                  ></rect>
                </g>
              </g>
            </g>
          </svg>
        </div>
      )}
    </ButtonBase>
  )
}
Button.propTypes = {
  variant: PropTypes.oneOf(["yellow", "white", "blue", "lightblue"]),
  env: PropTypes.oneOf(["yellow", "white", "blue"]),
  size: PropTypes.string,
  arrow: PropTypes.bool,
}

export const Container = tw.div`flex flex-col mx-auto max-w-screen-xl px-4`

export const Section = ({ children, bg, swoop = false }) => {
  return (
    <section
      css={[
        tw`py-8 md:py-12`,
        bg === "grey" && tw`bg-lightgrey`,
        bg === "blue" && tw`bg-blue`,
        swoop === true && tw`rounded-tl-regular rounded-br-regular`,
      ]}
    >
      {children}
    </section>
  )
}

export const Main = tw.main`flex-grow px-4`

export const PageHeading = ({ children, color = "blue", center }) => {
  return (
    <h2
      css={[
        color === "yellow" && tw`text-yellow`,
        color === "blue" && tw`text-blue`,
        center && tw`text-center`,
        tw`font-bold text-2xl leading-tight mb-0 md:(text-4xl leading-normal)`,
      ]}
    >
      {" "}
      {children}
    </h2>
  )
}

export const PageSubHeading = ({ children, el = "h3" }) => {
  return (
    <h3 tw="font-bold text-xl text-blue leading-tight mt-6 first:mt-0 md:(text-3xl leading-normal max-w-3xl) lg:(max-w-6xl)">
      {" "}
      {children}
    </h3>
  )
}

export const PageHeading3 = tw.h3`font-bold text-xl text-blue leading-tight mt-6 first:mt-0 md:(text-2xl leading-normal max-w-3xl) lg:(max-w-6xl)`

export const Table = tw.table`table-auto w-full mt-4 md:mt-4 rounded-tl-lg`

export const DiscList = ({ paddingLeftZero, sublist, isi, children, ...props }) => (
  <ul
    css={[
      tw`list-disc pl-12 lg:(pl-6)`,
      `li ul li { position: relative; padding-left: 1.25rem; &::before { content: "-"; position: absolute; left: 0; } }`,
      sublist && tw`list-none`,
      paddingLeftZero && tw`pl-[5px] lg:(pl-[20px])`,
      isi && tw`pl-5`
    ]}
    {...props}
  >
    {children}
  </ul>
)

export const Card = ({
  image,
  title,
  children,
  bgColor,
  mobileHidden,
  marginBottom,
}) => {
  return (
    <div
      css={[
        tw`bg-lightgrey px-6 pb-8 mt-16 text-center rounded-tl-regular rounded-br-regular flex flex-col relative`,
        bgColor === "grey" && tw`bg-grey`,
        bgColor === "white" && tw`bg-white`,
        marginBottom && tw`mb-12 md:(mb-0)`,
        mobileHidden && tw`hidden md:(block)`,
      ]}
    >
      <img
        src={image}
        alt={title}
        css={[tw`mx-auto -mt-16 w-32 md:(w-40 -mt-20) xl:(w-48 -mt-24)`]}
      />
      <div tw="flex justify-center items-center">
        <h3 tw="mt-6 mb-4 text-blue font-bold text-2xl leading-tight md:mb-8 xl:text-4xl">
          {title}
        </h3>
      </div>
      {children}
    </div>
  )
}

// defaults to star: for other special symbols, use css code
// eg dagger, set content="\2020", double dagger="\2021"
// symbols like star and double dagger are already read as superscripts by the browser
// all other symbols (double dagger, letters, etc) will need the superscript flag.

export const Footnotes = ({
  content = "*",
  superscript = false,
  children,
  color,
  shadow,
  doubleDagger = false,
}) => (
  <div
    css={[
      doubleDagger
        ? `&::before{ content: "${content}"; font-size: 1em; position: absolute; left: -0.5rem; top: 1px; }`
        : `&::before{ content: "${content}"; position: absolute; left: -0.4rem; }`,
      superscript && `&::before{ font-size: 0.65em }`,
      shadow && `text-shadow: 0 0 4px black;`,
      tw`text-ms relative`,
      color && `color: ${color}`
    ]}
  >
    {children}
  </div>
)

export const FaqLink = ({ children, path, iconPosition, scroll, ...props }) => {
  const Component = scroll === true ? AnchorLink : "a"
  return (
    <Component
      {...props}
      className="group gtm-cta-click"
      onClick={() => navigate(path)}
      tw="flex items-center text-base md:(px-0) cursor-pointer"
    >
      {iconPosition === "left" ? (
        <>
          <img
            src={binocularIcon}
            alt=""
            tw="group-hover:(transition ease-in-out duration-300 transform scale-110 pointer-events-none) mr-2"
          />
          <span tw="pb-0 group-hover:font-bold pointer-events-none">
            {children}
          </span>
        </>
      ) : (
        <>
          <span tw="pb-0 mr-2 group-hover:font-bold pointer-events-none">
            {children}
          </span>
          <img
            src={binocularIcon}
            alt=""
            tw="group-hover:(transition ease-in-out duration-300 transform scale-110 pointer-events-none)"
          />
        </>
      )}
    </Component>
  )
}

const InlineLinkBase = tw.a`text-blue border-b-2 transition hover:(border-blue)`
export const InlineLink = ({
  env,
  children,
  href,
  label,
  target,
  onClick,
  internal,
  size,
  color,
  addClassForGTM,
  elementName,
  removeTabIndex,
  noUnderline,
  addedStyles = [],
}) => {
  return (
    <InlineLinkBase
      css={[
        env === "blue" && tw`text-white hover:(border-yellow)`,
        env === "lightgrey" && tw`border-grey`,
        env === "grey" && tw`border-blue hover:border-white`,
        size === "xl" && tw`text-xl`,
        noUnderline && tw`border-none`,
        color && `color: ${color}`,
        ...addedStyles,
      ]}
      href={href}
      aria-label={label}
      target={target}
      onClick={
        internal
          ? e => {
              e.preventDefault()
              navigate(href)
            }
          : onClick
      }
      className={`${addClassForGTM && `${addClassForGTM}`}`}
      data-elementname={elementName}
      tabIndex={removeTabIndex ? -1 : 0}
    >
      {children}
    </InlineLinkBase>
  )
}

export const openChat = () => {
  if (window) {
    const chatElement = document.getElementById('webchatv4');
    if (chatElement) {
      chatElement.style.display = 'block'; // or '' if you want it to revert to CSS default
    }
    window.TAIlor?.toggleOpen(true);
    window.RxDefineChat?.hideChat(false)

  }
}